import React, { useState, useEffect } from 'react';
import { getData } from '../api/ApiServices';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/slices/store';
import '../styles/PriceHistoryChangesPopup.css';

interface PriceHistoryChangesPopupProps {
  isOpen: boolean;
  onClose: () => void;
  partId: string;
}

interface PriceHistoryItem {
  _id: string;
  supplierId: string;
  partId: string;
  currentPrice: number;
  previousPrice: number;
  createdAt: string;
  updatedAt: string;
  partDetails?: {
    internalPartNo: string;
    partName: string;
  };
  supplierparts?: {
    name: string;
  };
}

interface PriceHistoryGroup {
  count: number;
  totalpricehistories: PriceHistoryItem[];
  supplierId: string;
  partId: string;
}

const PriceHistoryChangesPopup: React.FC<PriceHistoryChangesPopupProps> = ({
  isOpen,
  onClose,
  partId
}) => {
  const [priceData, setPriceData] = useState<PriceHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [partDetails, setPartDetails] = useState<{
    internalPartNo: string;
    partName: string;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchPriceHistoryData();
    }
  }, [isOpen, partId]);

  const dateFormatDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const calculateChange = (currentPrice: number, previousPrice: number) => {
    return currentPrice - previousPrice;
  };
  
  const filters = useSelector((state: RootState) => state.filters);
  
  const fetchPriceHistoryData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        filter: {
          ...filters,
          pricehistorychanges: true
        }
      };
      
      console.log("Fetching price history data with params:", params);
      
      const response = await getData<{
        data: {
          data: PriceHistoryGroup[];
        };
      }>(`/pricehistory`, params);
        
      console.log("Price history changes response:", response);
      
      if (response && response.data && response.data.data) {
        const allPriceHistories: PriceHistoryItem[] = [];
        
        response.data.data.forEach(group => {
            console.log(group.partId);
            console.log(partId);
            
            
            if (group.partId === partId && group.totalpricehistories && group.totalpricehistories.length > 0) {
              allPriceHistories.push(...group.totalpricehistories);
              
              if (!partDetails && group.totalpricehistories[0].partDetails) {
                setPartDetails({
                  internalPartNo: group.totalpricehistories[0].partDetails.internalPartNo,
                  partName: group.totalpricehistories[0].partDetails.partName
                });
              }
            }
          });
                  
        const sortedData = allPriceHistories.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        setPriceData(sortedData);
      } else {
        setPriceData([]);
      }
    } catch (err) {
      console.error('Error fetching price history data:', err);
      setError('Failed to load price history details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="price-popup-overlay" onClick={onClose}>
      <div className="price-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="price-popup-header">
          <div className="price-popup-title">
            {partDetails 
              ? `Price History Changes for ${partDetails.internalPartNo} - ${partDetails.partName}`
              : 'Price History Changes'}
          </div>
          <button className="price-close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="price-error-message">{error}</div>}

        {isLoading ? (
          <div className="price-loader">Loading price history details...</div>
        ) : priceData.length === 0 ? (
          <div className="price-no-data">No price history records found for this part.</div>
        ) : (
          <table className="price-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Supplier</th>
                <th className="price-right-align">Current Price</th>
                <th className="price-right-align">Previous Price</th>
                <th className="price-right-align">Change</th>
              </tr>
            </thead>
            <tbody>
              {priceData.map((item) => {
                const priceChange = calculateChange(item.currentPrice, item.previousPrice);
                
                return (
                  <tr key={item._id}>
                    <td>{dateFormatDDMMYYYY(item.updatedAt)}</td>
                    <td>{item.supplierparts?.name || 'Unknown Supplier'}</td>
                    <td className="price-right-align">{formatCurrency(item.currentPrice)}</td>
                    <td className="price-right-align">{formatCurrency(item.previousPrice)}</td>
                    <td className={`price-right-align ${priceChange > 0 ? 'price-positive-change' : 
                                            priceChange < 0 ? 'price-negative-change' : ''}`}>
                      {formatCurrency(priceChange)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PriceHistoryChangesPopup;